import React, { useEffect, useState } from 'react'
import { useShow, IResourceComponentsProps, useGetIdentity, useOne, useNavigation } from '@pankod/refine-core'
import { Radio } from 'antd'
import type { RadioChangeEvent } from 'antd'
import type { ColumnsType } from 'antd/lib/table'
import { IQuestion, IWorkRequest } from 'interfaces'
import { EditDraftQuestion } from './edit'
import ReactMarkdown from 'react-markdown'

import {
  Space,
  Table,
  Tag,
  Progress,
  Breadcrumb,
  Tooltip,
  Icons,
  Button,
  PageHeader,
  Typography,
  Skeleton,
  Descriptions,
  Divider,
  EditButton,
  Image,
  useModalForm,
} from '@pankod/refine-antd'
import {
  createLogForApproval,
  createLogForReview,
  createLogForReviewRequireAmending,
  createLogOpenRequireAmending,
  createLogPublished,
  createLogRejectedAtApproval,
  createLogRejectedAtRewiew,
  createNotificationForApproval,
  createNotificationForReview,
  createNotificationForReviewRequireAmending,
  createNotificationOpenRequireAmending,
  createNotificationRejectedAtApproval,
  createNotificationRejectedAtRewiew,
  updateQuestionApprover,
  updateQuestionReviewer,
  updateQuestionStatus,
} from 'gql/mutations'
import metaData from 'metadata/questions'
import wr_metadata from 'metadata/workrequests'
import { CompletionStatus, getWorkRequestStatusCount } from 'helpers'
import { nhost } from 'utility'
import {
  createMailForApproval,
  createMailForReview,
  createMailForReviewRequireAmending,
  createMailOpenRequireAmending,
  createMailRejectedAtApproval,
  createMailRejectedAtRewiew,
} from 'helpers'
import QuestionLogs from 'components/questionlogs'
import Permissions from 'permissions'
import { useUserContext } from 'context/user'
import { getUsersByRole } from 'users'

type Permission = {
  granted?: boolean
}
interface romanType {
  [key: number]: string
}
interface alphabetsType {
  [key: number]: string
}

const roman: romanType = { 1: 'i', 2: 'ii', 3: 'iii', 4: 'iv', 5: 'v', 6: 'vi', 7: 'vii', 8: 'viii' }
const alphabets: alphabetsType = { 1: 'a', 2: 'b', 3: 'c', 4: 'd', 5: 'e', 6: 'f', 7: 'g', 8: 'h' }
interface MCQDataType {
  option_text: string
  option_image_id: string
  option_image_name: string
  option_insight: string
  option_flag: string
}

interface MatchDataType {
  left_label_text: string
  right_label_text: string
  option_insight: string
  option_comments: string
}

interface FillDataType {
  right_answer: string
  option_comments: string
}

interface ShortAnswerDataType {
  component_name: string
  rating_1: string
  rating_2: string
  rating_3: string
  rating_4: string
  weightage: string
}

const MCQColumns: ColumnsType<MCQDataType> = [
  {
    title: 'Option ',
    dataIndex: 'option_text',
    render: (_, record) => {
      if (record.option_image_id) {
        const publicUrl = nhost.storage.getPublicUrl({ fileId: record?.option_image_id })
        return <>{publicUrl && <Image width={100} src={publicUrl} />}</>
      } else {
        return <>{record?.option_text}</>
      }
    },
  },
  {
    title: 'Option Flag',
    dataIndex: 'option_flag',
    render: (_, { option_flag }) => {
      let color = 'geekblue'
      if (option_flag === 'Right Answer' || option_flag === 'RA') {
        color = 'green'
      }
      if (option_flag === 'Most Wrong' || option_flag === 'WW') {
        color = 'volcano'
      }
      return <Tag color={color}>{option_flag.toUpperCase()}</Tag>
    },
  },
  {
    title: 'Option Insight',
    dataIndex: 'option_insight',
  },
  {
    title: 'Reviewer Comments',
    dataIndex: 'reviewer_comments',
  },
  {
    title: 'Approver Comments',
    dataIndex: 'approver_comments',
  },
]

const MatchColumns: ColumnsType<MatchDataType> = [
  {
    title: 'Left Label',
    dataIndex: 'left_label_text',
  },
  {
    title: 'Right Label',
    dataIndex: 'right_label_text',
  },
  {
    title: 'Lebel Insight',
    dataIndex: 'option_insight',
  },
  {
    title: 'Reviewer Comments',
    dataIndex: 'reviewer_comments',
  },
  {
    title: 'Approver Comments',
    dataIndex: 'approver_comments',
  },
]

const FillColumns: ColumnsType<FillDataType> = [
  {
    title: 'Right Answer',
    dataIndex: 'right_answer',
    render: (text, record, index) => <>{`${roman[index + 1]}) ${text}`}</>,
  },
  {
    title: 'Reviewer Comments',
    dataIndex: 'reviewer_comments',
  },
  {
    title: 'Approver Comments',
    dataIndex: 'approver_comments',
  },
]

const ShortAnswerColumns: ColumnsType<ShortAnswerDataType> = [
  {
    title: 'Component Name',
    dataIndex: 'component_name',
  },
  {
    title: 'Rating 1',
    dataIndex: 'rating_1',
  },
  {
    title: 'Rating 2',
    dataIndex: 'rating_2',
  },
  {
    title: 'Rating 3',
    dataIndex: 'rating_3',
  },
  {
    title: 'Rating 4',
    dataIndex: 'rating_4',
  },
  {
    title: 'Weightage',
    dataIndex: 'weightage',
  },
]

export const DraftQuestionShow: React.FC<IResourceComponentsProps> = () => {
  const { data: user } = useGetIdentity()
  const role = user?.defaultRole
  const [qreviewewList, setReviewerList] = useState([] as any[])
  const [qapproverList, setApproverList] = useState([] as any[])
  const [qcreatorList, setCreatorList] = useState([] as any[])
  const [vcreatorList, setVariantCreatorList] = useState([] as any[])
  const { userRole } = useUserContext()
  const { show, push } = useNavigation()
  const [questionStatus, setQuestionStatus] = useState<string>('')
  const [reviewers, setReviewers] = useState<string[]>([])
  const [approvers, setApprovers] = useState<string[]>([])

  const [readPermission, setReadPermission] = useState<Permission>({})
  const [createPermission, setCreatePermission] = useState<Permission>({})
  const [editPermission, setEditPermission] = useState<Permission>({})
  const [deletePermission, setDeletePermission] = useState<Permission>({})
 
  useEffect(() => {
    getUsersByRole('qreviewer').then((response: any) => {
      setReviewerList(response.authUserRoles)
    })
  }, [])
  useEffect(() => {
    getUsersByRole('qapprover').then((response: any) => {
      setApproverList(response.authUserRoles)
    })
  }, [])
  useEffect(() => {
    getUsersByRole('qcreator').then((response: any) => {
      setCreatorList(response.authUserRoles)
    })
  }, [])
  useEffect(() => {
    getUsersByRole('variantcreator').then((response: any) => {
      setVariantCreatorList(response.authUserRoles)
    })
  }, [])
  useEffect(() => {
    if (userRole !== undefined) {
      setReadPermission(Permissions.can(userRole).readAny('draft_questions'))
      setCreatePermission(Permissions.can(userRole).createAny('draft_questions'))
      setEditPermission(Permissions.can(userRole).updateAny('draft_questions'))
      setDeletePermission(Permissions.can(userRole).deleteAny('draft_questions'))
    }
  }, [userRole])

  useEffect(() => {
    if (!readPermission?.granted && readPermission?.granted !== undefined && userRole !== 'admin' && userRole !== 'qbincharge') push('/')
    console.log(readPermission, 'readpermission')
  }, [readPermission])

  const { queryResult, setShowId } = useShow<IQuestion>({
    metaData,
  })

  const { data, isLoading } = queryResult
  const record = data?.data

  const { data: workRequestRecord } = useOne<IWorkRequest>({
    resource: 'work_requests',
    id: record?.work_request_id || '',
    metaData: wr_metadata,
    queryOptions: {
      enabled: !!record?.work_request_id,
    },
  })

  useEffect(() => {
    if (workRequestRecord?.data) setReviewers(workRequestRecord?.data?.reviewer_ids ?? [])
  }, [workRequestRecord])

  useEffect(() => {
    if (workRequestRecord?.data && record) {
      const approverIds = workRequestRecord?.data?.approver_ids?.filter((list: any) => list?.reviwer_id_selected === record?.reviewer_id)
      if (approverIds && approverIds[0]) setApprovers(approverIds[0]?.approver_ids ?? [])
    }
  }, [workRequestRecord, record])

  const [value, setValue] = useState(1)

  const {
    formProps: editFormProps,
    modalProps: editModalProps,
    show: editShow,
    close,
  } = useModalForm({
    resource: 'questions',
    action: 'edit',
    metaData,
    redirect: false,
  })

  const onChange = (e: RadioChangeEvent) => {
    setValue(e.target.value)
  }

  const handleQUestionStatusChange = (status: string) => {
    if (record?.id) {
      if (status === 'For Review' && reviewers && reviewers?.length > 0) {
        const { for_review_count, reviewed_count, approved_count, for_approval_count, rejected_count, retired_count } = getWorkRequestStatusCount(
          workRequestRecord?.data
        )
        const reviewerIndex =
          (for_review_count + reviewed_count + rejected_count + retired_count + approved_count + for_approval_count) % reviewers?.length
        const reviewerId = workRequestRecord?.data?.reviewer_ids[reviewerIndex]
        if (reviewerId) {
          let reviewerDetails = qreviewewList.filter((reviewers: any) => reviewers?.user?.id === reviewerId)[0]
          updateQuestionStatus(record.id, status)
            .then(() => updateQuestionReviewer(record.id, reviewerId))
            .then(() => createNotificationForReview(user?.id, reviewerId, workRequestRecord?.data, record?.question_id))
            .then(() => createLogForReview(reviewerId, workRequestRecord?.data, record?.id))
            .then(() => CompletionStatus(workRequestRecord?.data))
            .then(() => createMailForReview(workRequestRecord?.data, record?.question_id,reviewerDetails?.user?.displayName,reviewerDetails?.user?.email))
          setQuestionStatus(status)
        }
      }
      if (status === 'For Approval' && approvers && approvers?.length > 0) {
        const { for_approval_count, approved_count, rejected_count, retired_count } = getWorkRequestStatusCount(workRequestRecord?.data)
        const approverIndex = (for_approval_count + approved_count + rejected_count + retired_count) % approvers?.length
        const approverId = approvers[approverIndex]
        if (approverId) {
        let approverDetails = qapproverList.filter((appUser: any) => appUser?.user?.id === approverId)[0]

          updateQuestionStatus(record.id, status)
            .then(() => updateQuestionApprover(record.id, approverId))
            .then(() => createNotificationForApproval(user?.id, approverId, workRequestRecord?.data, record?.question_id))
            .then(() => createLogForApproval(record?.reviewer_id, approverId, workRequestRecord?.data, record?.id))
            .then(() => CompletionStatus(workRequestRecord?.data))
            .then(() => createMailForApproval(workRequestRecord?.data, record?.question_id,approverDetails?.user?.displayName,approverDetails?.user?.email))
          setQuestionStatus(status)
        }
      }
      if (status !== 'For Review' && status !== 'For Approval') {
        updateQuestionStatus(record.id, status).then(() => CompletionStatus(workRequestRecord?.data))
        setQuestionStatus(status)
      }
    }
  }

  useEffect(() => {
    if (record?.status) setQuestionStatus(record?.status)
  }, [record])

  return (
    <PageHeader
      className="site-page-header-responsive"
      ghost={false}
      title={`#${record?.question_id ?? ''} - ${record?.grade?.name ?? ''} ${record?.topic?.sub_theme?.theme?.subject?.name ?? ''} ${
        record?.question_type?.name
      }`}
      extra={[
        (role === 'admin' || role === 'qbincharge' || role === 'subjectincharge') && <QuestionLogs questionId={record?.id} />,
        <Button key="close" onClick={() => window.history.back()} icon={<Icons.LeftOutlined />}>
          Back
        </Button>,
        role === 'qcreator' && (
          <Button
            key="submit for review"
            onClick={() => {
              handleQUestionStatusChange('For Review')
            }}
            disabled={(questionStatus !== 'Open' && questionStatus !== 'Open (Require Amending)') || reviewers?.length < 1}
          >
            Submit for Review
          </Button>
        ),
        role === 'qreviewer' && (
          <Button
            key="rejected at review"
            onClick={() => {
              let creatorDetails = []
              if (workRequestRecord?.data?.request_type === 'Q') {
                creatorDetails = qcreatorList.filter((item: any) => item?.user?.id === record?.creator_id)[0]
              }
              if (workRequestRecord?.data?.request_type === 'V') {
                creatorDetails = vcreatorList.filter((item: any) => item?.user?.id === record?.creator_id)[0]
              }
              handleQUestionStatusChange('Rejected at Review')
              record && createNotificationRejectedAtRewiew(user?.id, record?.creator_id, workRequestRecord?.data, record?.question_id)
              record && createLogRejectedAtRewiew(record?.reviewer_id, workRequestRecord?.data, record?.id)
              createMailRejectedAtRewiew(workRequestRecord?.data, record?.question_id,creatorDetails?.user?.displayName,creatorDetails?.user?.name)
            }}
            disabled={questionStatus !== 'For Review' && questionStatus !== 'For Review (Require Amending)'}
          >
            Reject
          </Button>
        ),
        role === 'qreviewer' && (
          <Button
            key="require amending at review"
            onClick={() => {
              let creatorDetails = []
              if (workRequestRecord?.data?.request_type === 'Q') {
                creatorDetails = qcreatorList.filter((item: any) => item?.user?.id === record?.creator_id)[0]
              }
              if (workRequestRecord?.data?.request_type === 'V') {
                creatorDetails = vcreatorList.filter((item: any) => item?.user?.id === record?.creator_id)[0]
              }
              handleQUestionStatusChange('Open (Require Amending)')
              record && createNotificationOpenRequireAmending(user?.id, record?.creator_id, workRequestRecord?.data, record?.question_id)
              record && createLogOpenRequireAmending(record?.reviewer_id, record?.creator_id, workRequestRecord?.data, record?.id)
              createMailOpenRequireAmending(workRequestRecord?.data, record?.question_id,creatorDetails?.user?.displayName,creatorDetails?.user?.email)
            }}
            disabled={questionStatus !== 'For Review' && questionStatus !== 'For Review (Require Amending)'}
          >
            Require Amending
          </Button>
        ),
        role === 'qreviewer' && (
          <Button
            key="submit to approval"
            onClick={() => handleQUestionStatusChange('For Approval')}
            disabled={(questionStatus !== 'For Review' && questionStatus !== 'For Review (Require Amending)') || approvers?.length < 1}
          >
            Submit for Approval
          </Button>
        ),
        role === 'qapprover' && (
          <Button
            key="rejected at approval"
            onClick={() => {
        let reviewerDetails = qreviewewList.filter((reviewers: any) => reviewers?.user?.id === record?.reviewer_id)[0]

              handleQUestionStatusChange('Rejected at Approval')
              record &&
                createNotificationRejectedAtApproval(user?.id, record?.creator_id, record?.reviewer_id, workRequestRecord?.data, record?.question_id)
              record && createLogRejectedAtApproval(record?.approver_id, workRequestRecord?.data, record?.id)
              createMailRejectedAtApproval(workRequestRecord?.data, record?.question_id,reviewerDetails?.user?.displayName,reviewerDetails?.user?.email)
            }}
            disabled={questionStatus !== 'For Approval'}
          >
            Reject
          </Button>
        ),
        role === 'qapprover' && (
          <Button
            key="require amending at approval"
            onClick={() => {
        let reviewerDetails = qreviewewList.filter((reviewers: any) => reviewers?.user?.id === record?.reviewer_id)[0]

              handleQUestionStatusChange('For Review (Require Amending)')
              record && createNotificationForReviewRequireAmending(user?.id, record?.reviewer_id, workRequestRecord?.data, record?.question_id)
              record && createLogForReviewRequireAmending(record?.approver_id, record?.reviewer_id, workRequestRecord?.data, record?.id)
              createMailForReviewRequireAmending(workRequestRecord?.data, record?.question_id,reviewerDetails?.user?.displayName,reviewerDetails?.user?.email)
            }}
            disabled={questionStatus !== 'For Approval'}
          >
            Require Amending
          </Button>
        ),
        role === 'qapprover' && (
          <Button
            key="approved"
            onClick={() => {
              handleQUestionStatusChange('Published')
              record && createLogPublished(record?.approver_id, workRequestRecord?.data, record?.id)
            }}
            disabled={questionStatus !== 'For Approval'}
          >
            Approve
          </Button>
        ),
        role === 'admin' ||
        (role === 'qcreator' && (questionStatus === 'Open' || questionStatus === 'Open (Require Amending)')) ||
        (role === 'qreviewer' && (questionStatus === 'For Review' || questionStatus === 'For Review (Require Amending)')) ||
        (role === 'qapprover' && questionStatus === 'For Approval') ? (
          <EditButton
            key="edit"
            disabled={record?.status === 'Published' || record?.status === 'Retired'}
            icon={<Icons.EditOutlined />}
            type="primary"
            recordItemId={record?.id}
            onClick={() => {
              editShow(record?.id)
            }}
          >
            Edit
          </EditButton>
        ) : null,
      ]}
    >
      <Descriptions size="small" column={1}>
        <Descriptions.Item label="">
          <Tag
            color={
              questionStatus === 'Published'
                ? 'green'
                : questionStatus === 'Rejected at Review' || questionStatus === 'Rejected at Approval' || questionStatus === 'Retired'
                ? 'volcano'
                : questionStatus === 'Open (Require Amending)' || questionStatus === 'For Review (Require Amending)'
                ? 'pink'
                : 'geekblue'
            }
          >
            {questionStatus}
          </Tag>
        </Descriptions.Item>
      </Descriptions>
      <Descriptions size="small" column={3}>
        <Descriptions.Item label="Work Request">
          <Button
            size="small"
            type="link"
            onClick={() => {
              if (record?.work_request_id) show('work_requests', record?.work_request_id)
            }}
          >
            {`#${record?.work_request?.request_id ?? ''} ${record?.work_request?.name ?? ''}`}
          </Button>
        </Descriptions.Item>

        <Descriptions.Item label="Grade">{record?.grade?.name}</Descriptions.Item>
        <Descriptions.Item label="Subject">{record?.subject?.name}</Descriptions.Item>
        <Descriptions.Item label="Theme">{record?.theme?.name}</Descriptions.Item>
        <Descriptions.Item label="Sub Theme">{record?.sub_theme?.name}</Descriptions.Item>
        <Descriptions.Item label="Topic">{record?.topic?.name}</Descriptions.Item>
        <Descriptions.Item label="Question Type">{record?.question_type?.name}</Descriptions.Item>
      </Descriptions>
      <Divider style={{ margin: '0' }} />
      <b>Tags:</b>
      <Descriptions size="small" column={3}>
        <Descriptions.Item label="Competency">{record?.competencies?.name}</Descriptions.Item>
        <Descriptions.Item label="TN Taxonomy">{record?.tn_taxonomy?.name}</Descriptions.Item>
        <Descriptions.Item label="Teacher Level">{record?.teacher_level?.name}</Descriptions.Item>
        <Descriptions.Item label="Strand">{record?.strand?.name}</Descriptions.Item>
        <Descriptions.Item label="Bloom Level">{record?.bloom_level?.name}</Descriptions.Item>
        <Descriptions.Item label="Difficulty">{record?.difficulty_level?.name}</Descriptions.Item>
        <Descriptions.Item label="Report Theme">{record?.report_theme?.name}</Descriptions.Item>
        {/* <Descriptions.Item label="Centa Standard">{record?.centa_standard?.name}</Descriptions.Item> */}
        <Descriptions.Item label="Centa Concept">{record?.centa_concept?.name}</Descriptions.Item>
        <Descriptions.Item label="Explanation Type">{record?.explanation_type?.name}</Descriptions.Item>
      </Descriptions>
      <Divider style={{ margin: ' 0' }} />
      <b>Users:</b>
      <Descriptions size="small" column={3}>
        <Descriptions.Item label="Creator">{record?.userByCreatorId?.displayName}</Descriptions.Item>
        {role !== 'qcreator' && <Descriptions.Item label="Reviewer">{record?.userByReviewerId?.displayName}</Descriptions.Item>}
        {role !== 'qcreator' && role !== 'qreviewer' && (
          <Descriptions.Item label="Approver">{record?.userByApproverId?.displayName}</Descriptions.Item>
        )}
      </Descriptions>
      <Divider style={{ margin: ' 0' }} />
      {record?.parent_question && (
        <>
          <b>Parent Question:</b>
          <Descriptions size="small" column={1} layout="vertical">
            <Descriptions.Item>{`${record?.parent_question?.question_id}. ${record?.parent_question?.title}`}</Descriptions.Item>
          </Descriptions>
        </>
      )}
      <b>Question:</b>
      <Descriptions size="small" column={1} layout="vertical">
        <Descriptions.Item>
          <ReactMarkdown>{`${record?.title}`}</ReactMarkdown>
        </Descriptions.Item>
      </Descriptions>

      {record?.option_details && record?.question_type_id === 'MCQ' && (
        <Radio.Group onChange={onChange} value={value}>
          {record?.option_details?.map((option: any, idx: number) => {
            if (option.option_image_id) {
              const publicUrl = nhost.storage.getPublicUrl({ fileId: option?.option_image_id })
              return (
                <Radio key={idx} value={idx + 1}>
                  {publicUrl && <img width={100} src={publicUrl} />}
                </Radio>
              )
            } else {
              return (
                <Radio key={idx} value={idx + 1}>
                  {option?.option_text}
                </Radio>
              )
            }
          })}
        </Radio.Group>
      )}
      {record?.option_details && record?.question_type_id === 'Match' && (
        <div>
          <Table
            style={{ display: 'inline-block', minWidth: '300px' }}
            showHeader={false}
            size="small"
            bordered
            columns={[
              {
                title: 'Left Label',
                dataIndex: 'left_label_text',
                render: (text, record, index) => <>{`${roman[index + 1]}) ${text}`}</>,
              },
              {
                title: 'Right Label',
                dataIndex: 'right_label_text',
                render: (text, record, index) => <>{`${alphabets[index + 1]}) ${text}`}</>,
              },
            ]}
            dataSource={record?.option_details}
            pagination={false}
          />
        </div>
      )}
      <Divider style={{ margin: '12px 0' }} />

      {record?.question_type_id === 'ShortAnswer' && (
        <>
          <b>Expected points in the answer:</b>
          <p>{record?.description}</p>
          <Divider style={{ margin: '12px 0' }} />
        </>
      )}
      {record?.option_details && (
        <>
          {record?.question_type_id === 'MCQ' && (
            <>
              <b>Option Details:</b>
              <Table size="small" columns={MCQColumns} dataSource={record?.option_details} pagination={false} />
            </>
          )}
          {record?.question_type_id === 'Match' && (
            <>
              <b>Label Details:</b>
              <Table size="small" columns={MatchColumns} dataSource={record?.option_details} pagination={false} />
            </>
          )}
          {record?.question_type_id === 'Fill' && (
            <>
              <b>Answer Details:</b>
              <Table size="small" columns={FillColumns} dataSource={record?.option_details} pagination={false} />
            </>
          )}
          {record?.question_type_id === 'ShortAnswer' && (
            <>
              <b>Rubric Details:</b>
              <Table size="small" columns={ShortAnswerColumns} dataSource={record?.option_details} pagination={false} />
            </>
          )}
        </>
      )}

      {!record && <Skeleton paragraph={{ rows: 1 }} />}
      <EditDraftQuestion modalProps={editModalProps} formProps={editFormProps} record={record} close={close} />
    </PageHeader>
  )
}
